// /admin/referral-payouts — the operator's view of ambassador (referral)
// payouts: what goes out at the next monthly run (the 7th), what's coming in
// the next month, how each ambassador stands, what needs a human, the monthly
// statements, and the full ledger with hold / release / void / retry / resolve.
//
// Data: GET /api/admin/referral-payouts/overview (summary, upcoming, artists,
// attention), GET /api/admin/referral-payouts (ledger) and /statements.
// Actions POST to /api/admin/referral-payouts/:id/<action> (or
// /statements/:id/retry); every one is audit-logged.

import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Menu,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminActionModal } from '../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../components/admin-shell/AdminShell';
import { EmptyState } from '../../components/ui';
import { StatCard } from '../../components/ui/StatCard';
import { showToast } from '../../lib/toast';
import { adminGet, adminPost } from './_api';

type PayoutState = 'accrued' | 'pending' | 'held' | 'paid' | 'failed' | 'void';
type AccountStatus = 'not_started' | 'onboarding' | 'active' | 'restricted';

type Overview = {
  currency: string;
  switches: { payoutsLive: boolean; stripeConfigured: boolean; nextRunAt: string; nextRunCoversMonth: string };
  summary: {
    readyMinor: number;
    readyCount: number;
    laterMinor: number;
    blockedMinor: number;
    blockedCount: number;
    heldMinor: number;
    failedMinor: number;
    paidThisMonthMinor: number;
    paidAllTimeMinor: number;
    forecast30Minor: number;
  };
  upcoming: Array<{
    venueAccountName: string;
    artistName: string;
    renewsAt: string;
    kind: 'renewal' | 'trial_ends';
    expectedPayoutMinor: number;
  }>;
  artists: Array<{
    userId: string;
    name: string;
    email: string | null;
    referredVenues: number;
    liveVenues: number;
    earnedMinor: number;
    paidMinor: number;
    owedMinor: number;
    lastPaidAt: string | null;
    payoutAccountStatus: AccountStatus;
    payoutsEnabled: boolean;
  }>;
  attention: Array<{
    payoutId: string;
    artistName: string;
    venueAccountName: string;
    payoutMinor: number;
    state: PayoutState;
    reason: string;
  }>;
};

type LedgerRow = {
  id: string;
  referring_artist: { user_id: string; display_name: string; email: string | null };
  venue_account_name: string;
  gross_amount_minor: number;
  base_amount_minor: number | null;
  currency: string;
  payout_amount_minor: number;
  state: PayoutState;
  earned_month: string | null;
  statement_id: string | null;
  attempts: number;
  last_error: string | null;
  hold_reason: string | null;
  void_reason: string | null;
  attention_reason: string | null;
  stripe_transfer_id: string | null;
  accrued_at: string;
  paid_at: string | null;
  payout_account_status: AccountStatus;
  payouts_enabled: boolean;
};

type StatementRow = {
  id: string;
  period_month: string;
  artist: { user_id: string; display_name: string; email: string | null };
  currency: string;
  total_minor: number;
  payout_count: number;
  state: 'pending' | 'paid' | 'failed' | 'void';
  attempts: number;
  last_error: string | null;
  stripe_transfer_id: string | null;
  created_at: string;
  paid_at: string | null;
};

type Action = 'hold' | 'release' | 'void' | 'retry' | 'resolve';

export const Route = createFileRoute('/admin/referral-payouts')({
  component: AdminReferralPayouts,
});

function money(minor: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(minor / 100);
}

function date(iso: string | null): string {
  if (iso === null) return '—';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Brisbane',
  }).format(new Date(iso));
}

/** `2026-10` → `October 2026`. */
function monthName(key: string | null): string {
  if (key === null) return '—';
  const [y, m] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)),
  );
}

const STATE_LABEL: Record<PayoutState, { label: string; color: string }> = {
  accrued: { label: 'Owed', color: 'gray' },
  pending: { label: 'Sending', color: 'sun' },
  held: { label: 'On hold', color: 'orange' },
  paid: { label: 'Paid', color: 'brand' },
  failed: { label: 'Failed', color: 'red' },
  void: { label: 'Cancelled', color: 'gray' },
};

const STATEMENT_LABEL: Record<StatementRow['state'], { label: string; color: string }> = {
  pending: { label: 'Sending', color: 'sun' },
  paid: { label: 'Paid', color: 'brand' },
  failed: { label: 'Failed', color: 'red' },
  void: { label: 'Cancelled', color: 'gray' },
};

const ACCOUNT_LABEL: Record<AccountStatus, { label: string; color: string }> = {
  not_started: { label: 'Not set up', color: 'gray' },
  onboarding: { label: 'Setting up', color: 'sun' },
  restricted: { label: 'Needs info', color: 'orange' },
  active: { label: 'Ready', color: 'brand' },
};

/** Which actions make sense for a payout in this state. */
function actionsFor(state: PayoutState, needsAttention: boolean): Action[] {
  const actions: Action[] = [];
  if (state === 'failed') actions.push('retry');
  if (state === 'held') actions.push('release');
  if (state === 'accrued' || state === 'pending' || state === 'failed') actions.push('hold');
  if (state !== 'paid' && state !== 'void') actions.push('void');
  if (needsAttention) actions.push('resolve');
  return actions;
}

const ACTION_COPY: Record<Action, { menu: string; title: string; confirm: string; color?: string; input?: 'reason' | 'note' }> = {
  hold: { menu: 'Put on hold', title: 'Put this payout on hold', confirm: 'Hold', input: 'reason' },
  release: { menu: 'Release hold', title: 'Release this payout', confirm: 'Release' },
  void: { menu: 'Cancel payout', title: 'Cancel this payout', confirm: 'Cancel payout', color: 'red', input: 'reason' },
  retry: { menu: 'Retry transfer', title: 'Retry this transfer', confirm: 'Retry' },
  resolve: { menu: 'Mark resolved', title: 'Mark this as resolved', confirm: 'Resolve', input: 'note' },
};

function csvOf(rows: LedgerRow[]): string {
  const cell = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    'payout_id', 'artist', 'artist_email', 'venue', 'state', 'venue_paid', 'ex_gst_base',
    'payout', 'currency', 'earned_month', 'accrued_at', 'paid_at', 'stripe_transfer_id', 'note',
  ];
  const lines = rows.map((r) =>
    [
      r.id,
      r.referring_artist.display_name,
      r.referring_artist.email,
      r.venue_account_name,
      r.state,
      (r.gross_amount_minor / 100).toFixed(2),
      r.base_amount_minor === null ? '' : (r.base_amount_minor / 100).toFixed(2),
      (r.payout_amount_minor / 100).toFixed(2),
      r.currency,
      r.earned_month,
      r.accrued_at,
      r.paid_at,
      r.stripe_transfer_id,
      r.attention_reason ?? r.hold_reason ?? r.void_reason ?? r.last_error,
    ]
      .map(cell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\n');
}

function AdminReferralPayouts() {
  const qc = useQueryClient();
  const [ledgerFilter, setLedgerFilter] = useState<'all' | PayoutState>('all');
  const [pending, setPending] = useState<{ action: Action; payoutId: string; label: string } | null>(null);
  const [text, setText] = useState('');

  const overview = useQuery({
    queryKey: ['admin', 'referral-payouts', 'overview'],
    queryFn: () => adminGet<Overview>('/api/admin/referral-payouts/overview'),
  });
  const ledger = useQuery({
    queryKey: ['admin', 'referral-payouts', 'ledger'],
    queryFn: () => adminGet<{ items: LedgerRow[] }>('/api/admin/referral-payouts'),
  });
  const statements = useQuery({
    queryKey: ['admin', 'referral-payouts', 'statements'],
    queryFn: () => adminGet<{ items: StatementRow[] }>('/api/admin/referral-payouts/statements'),
  });
  const retryStatement = useMutation({
    mutationFn: (id: string) => adminPost(`/api/admin/referral-payouts/statements/${id}/retry`, {}),
    onSuccess: async () => {
      showToast({ title: 'Done', body: 'Statement queued to send again.', kind: 'success' });
      await qc.invalidateQueries({ queryKey: ['admin', 'referral-payouts'] });
    },
    onError: (err: Error) => showToast({ title: 'Could not retry', body: err.message, kind: 'error' }),
  });

  const act = useMutation({
    mutationFn: ({ action, payoutId, input }: { action: Action; payoutId: string; input: string }) => {
      const kind = ACTION_COPY[action].input;
      const body = kind === 'reason' ? { reason: input } : kind === 'note' ? { note: input } : {};
      return adminPost(`/api/admin/referral-payouts/${payoutId}/${action}`, body);
    },
    onSuccess: async (_d, vars) => {
      showToast({ title: 'Done', body: `${ACTION_COPY[vars.action].menu}: saved.`, kind: 'success' });
      setPending(null);
      setText('');
      await qc.invalidateQueries({ queryKey: ['admin', 'referral-payouts'] });
    },
  });

  const filtered = useMemo(() => {
    const rows = ledger.data?.items ?? [];
    return ledgerFilter === 'all' ? rows : rows.filter((r) => r.state === ledgerFilter);
  }, [ledger.data, ledgerFilter]);

  function downloadCsv() {
    const blob = new Blob([csvOf(filtered)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wwaave-referral-payouts-${ledgerFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function open(action: Action, payoutId: string, label: string) {
    act.reset();
    setText('');
    setPending({ action, payoutId, label });
  }

  function ActionMenu({ id, state, attention, label }: { id: string; state: PayoutState; attention: boolean; label: string }) {
    const actions = actionsFor(state, attention);
    if (actions.length === 0) return null;
    return (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <Button size="xs" variant="subtle">
            Actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {actions.map((a) => (
            <Menu.Item
              key={a}
              {...(ACTION_COPY[a].color !== undefined ? { color: ACTION_COPY[a].color } : {})}
              onClick={() => open(a, id, label)}
            >
              {ACTION_COPY[a].menu}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }

  const o = overview.data;
  const cur = o?.currency ?? 'AUD';
  const copy = pending !== null ? ACTION_COPY[pending.action] : null;

  return (
    <AdminShell>
      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={2}>Referral payouts</Title>
          <Text c="dimmed" size="sm">
            Ambassadors earn 10% of what a venue they referred pays (excluding GST), from the venue&apos;s
            first payment. Venues are charged on the 1st; ambassadors are paid once a month, on the 7th, for
            everything earned the month before (Brisbane time).
          </Text>
        </Stack>

        {o !== undefined && !o.switches.payoutsLive && (
          <Alert color="sun" title="Payouts are switched off">
            Earnings are tracked, but nothing is sent. Set the <Text component="code">referral_payouts_live</Text>{' '}
            feature to <b>All</b> to start transfers.
          </Alert>
        )}
        {o !== undefined && o.switches.payoutsLive && !o.switches.stripeConfigured && (
          <Alert color="red" title="Stripe isn't connected">
            Payouts are on, but no Stripe key is set, so nothing can be sent.
          </Alert>
        )}

        {overview.isLoading || o === undefined ? (
          <Loader />
        ) : (
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
            <StatCard
              label={`Next payout · ${date(o.switches.nextRunAt)}`}
              value={money(o.summary.readyMinor, cur)}
              hint={[
                `Earnings to the end of ${monthName(o.switches.nextRunCoversMonth)}`,
                o.summary.laterMinor > 0 ? `${money(o.summary.laterMinor, cur)} more the month after` : null,
                o.switches.payoutsLive ? null : 'waiting for payouts to be switched on',
              ]
                .filter((x) => x !== null)
                .join(' · ')}
            />
            <StatCard
              label="Waiting on artist setup"
              value={money(o.summary.blockedMinor, cur)}
              hint={`${o.summary.blockedCount} payout${o.summary.blockedCount === 1 ? '' : 's'} · the artist hasn't finished payout setup`}
              emphasis="sun"
            />
            <StatCard label="Next month (forecast)" value={money(o.summary.forecast30Minor, cur)} hint="From referred venues renewing" />
            <StatCard label="Paid this month" value={money(o.summary.paidThisMonthMinor, cur)} hint={`${money(o.summary.paidAllTimeMinor, cur)} all time`} emphasis="ink" />
            <StatCard label="On hold" value={money(o.summary.heldMinor, cur)} emphasis="ink" />
            <StatCard label="Failed" value={money(o.summary.failedMinor, cur)} hint="Retry or cancel below" emphasis={o.summary.failedMinor > 0 ? 'sun' : 'ink'} />
          </SimpleGrid>
        )}

        {o !== undefined && o.attention.length > 0 && (
          <Card withBorder radius="md" p="md">
            <Stack gap="sm">
              <Title order={4}>Needs attention ({o.attention.length})</Title>
              <Table verticalSpacing="xs">
                <Table.Tbody>
                  {o.attention.map((a) => (
                    <Table.Tr key={a.payoutId}>
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {a.artistName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {a.venueAccountName}
                        </Text>
                      </Table.Td>
                      <Table.Td>{money(a.payoutMinor, cur)}</Table.Td>
                      <Table.Td>
                        <Badge color={STATE_LABEL[a.state].color} variant="light" size="sm">
                          {STATE_LABEL[a.state].label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{a.reason}</Text>
                      </Table.Td>
                      <Table.Td align="right">
                        <ActionMenu
                          id={a.payoutId}
                          state={a.state}
                          attention={a.state !== 'failed' && a.state !== 'held'}
                          label={`${a.artistName} · ${money(a.payoutMinor, cur)}`}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Card>
        )}

        <Tabs defaultValue="upcoming" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="upcoming">Upcoming</Tabs.Tab>
            <Tabs.Tab value="artists">Ambassadors</Tabs.Tab>
            <Tabs.Tab value="statements">Monthly statements</Tabs.Tab>
            <Tabs.Tab value="ledger">Ledger</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="upcoming" pt="md">
            {o === undefined ? null : o.upcoming.length === 0 ? (
              <EmptyState title="Nothing due in the next month" body="Referred venues' renewals appear here with what each will earn." />
            ) : (
              <Table striped verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Venue</Table.Th>
                    <Table.Th>Ambassador</Table.Th>
                    <Table.Th>What happens</Table.Th>
                    <Table.Th ta="right">Expected payout</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {o.upcoming.map((u) => (
                    <Table.Tr key={`${u.venueAccountName}-${u.renewsAt}`}>
                      <Table.Td>{date(u.renewsAt)}</Table.Td>
                      <Table.Td>{u.venueAccountName}</Table.Td>
                      <Table.Td>{u.artistName}</Table.Td>
                      <Table.Td>{u.kind === 'trial_ends' ? 'Free trial ends — first payment' : 'Subscription renews'}</Table.Td>
                      <Table.Td ta="right">{money(u.expectedPayoutMinor, cur)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="artists" pt="md">
            {o === undefined ? null : o.artists.length === 0 ? (
              <EmptyState title="No ambassadors yet" body="Artists appear here once a venue signs up through their link." />
            ) : (
              <Table striped verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ambassador</Table.Th>
                    <Table.Th>Venues referred</Table.Th>
                    <Table.Th ta="right">Earned</Table.Th>
                    <Table.Th ta="right">Paid</Table.Th>
                    <Table.Th ta="right">Owed</Table.Th>
                    <Table.Th>Last paid</Table.Th>
                    <Table.Th>Payout setup</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {o.artists.map((a) => (
                    <Table.Tr key={a.userId}>
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {a.name}
                        </Text>
                        {a.email !== null && (
                          <Text size="xs" c="dimmed">
                            {a.email}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {a.referredVenues} ({a.liveVenues} subscribed)
                      </Table.Td>
                      <Table.Td ta="right">{money(a.earnedMinor, cur)}</Table.Td>
                      <Table.Td ta="right">{money(a.paidMinor, cur)}</Table.Td>
                      <Table.Td ta="right">
                        <Text fw={a.owedMinor > 0 ? 600 : 400} size="sm">
                          {money(a.owedMinor, cur)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{date(a.lastPaidAt)}</Table.Td>
                      <Table.Td>
                        <Badge color={ACCOUNT_LABEL[a.payoutAccountStatus].color} variant="light" size="sm">
                          {ACCOUNT_LABEL[a.payoutAccountStatus].label}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="statements" pt="md">
            {statements.isLoading ? (
              <Loader />
            ) : (statements.data?.items.length ?? 0) === 0 ? (
              <EmptyState
                title="No statements yet"
                body="On the 7th of each month, every ambassador who is owed money gets one statement and one transfer."
              />
            ) : (
              <Table striped verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Earnings for</Table.Th>
                    <Table.Th>Ambassador</Table.Th>
                    <Table.Th ta="right">Payouts</Table.Th>
                    <Table.Th ta="right">Total</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {statements.data?.items.map((st) => (
                    <Table.Tr key={st.id}>
                      <Table.Td>{monthName(st.period_month)}</Table.Td>
                      <Table.Td>{st.artist.display_name}</Table.Td>
                      <Table.Td ta="right">{st.payout_count}</Table.Td>
                      <Table.Td ta="right">{money(st.total_minor, st.currency)}</Table.Td>
                      <Table.Td>
                        <Badge color={STATEMENT_LABEL[st.state].color} variant={st.state === 'paid' ? 'filled' : 'light'} size="sm">
                          {STATEMENT_LABEL[st.state].label}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {st.paid_at !== null ? `Paid ${date(st.paid_at)}` : st.last_error ?? `Built ${date(st.created_at)}`}
                        </Text>
                      </Table.Td>
                      <Table.Td align="right">
                        {st.state === 'failed' && (
                          <Button
                            size="xs"
                            variant="subtle"
                            loading={retryStatement.isPending && retryStatement.variables === st.id}
                            onClick={() => retryStatement.mutate(st.id)}
                          >
                            Retry
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="ledger" pt="md">
            <Stack gap="sm">
              <Group justify="space-between" wrap="wrap">
                <SegmentedControl
                  size="xs"
                  value={ledgerFilter}
                  onChange={(v) => setLedgerFilter(v as typeof ledgerFilter)}
                  data={[
                    { value: 'all', label: 'All' },
                    { value: 'accrued', label: 'Owed' },
                    { value: 'held', label: 'On hold' },
                    { value: 'failed', label: 'Failed' },
                    { value: 'paid', label: 'Paid' },
                    { value: 'void', label: 'Cancelled' },
                  ]}
                />
                <Button size="xs" variant="light" onClick={downloadCsv} disabled={filtered.length === 0}>
                  Export CSV
                </Button>
              </Group>
              {ledger.isLoading ? (
                <Loader />
              ) : filtered.length === 0 ? (
                <EmptyState title="No payouts in this view" body="Each paid invoice from a referred venue adds a row here." />
              ) : (
                <Table striped verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Earned</Table.Th>
                      <Table.Th>Ambassador</Table.Th>
                      <Table.Th>Venue</Table.Th>
                      <Table.Th ta="right">Venue paid</Table.Th>
                      <Table.Th ta="right">Payout</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filtered.map((r) => {
                      const note = r.attention_reason ?? r.hold_reason ?? r.void_reason ?? r.last_error;
                      return (
                        <Table.Tr key={r.id}>
                          <Table.Td>
                            {date(r.accrued_at)}
                            {r.earned_month !== null && (
                              <Text size="xs" c="dimmed">
                                {monthName(r.earned_month)} earnings
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>{r.referring_artist.display_name}</Table.Td>
                          <Table.Td>{r.venue_account_name}</Table.Td>
                          <Table.Td ta="right">{money(r.gross_amount_minor, r.currency)}</Table.Td>
                          <Table.Td ta="right">{money(r.payout_amount_minor, r.currency)}</Table.Td>
                          <Table.Td>
                            <Badge color={STATE_LABEL[r.state].color} variant={r.state === 'paid' ? 'filled' : 'light'} size="sm">
                              {STATE_LABEL[r.state].label}
                            </Badge>
                            {r.paid_at !== null && (
                              <Text size="xs" c="dimmed">
                                {date(r.paid_at)}
                              </Text>
                            )}
                            {note !== null && (
                              <Text size="xs" c={r.attention_reason !== null ? 'orange' : 'dimmed'}>
                                {note}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td align="right">
                            <ActionMenu
                              id={r.id}
                              state={r.state}
                              attention={r.attention_reason !== null}
                              label={`${r.referring_artist.display_name} · ${money(r.payout_amount_minor, r.currency)}`}
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <AdminActionModal
        opened={pending !== null}
        onClose={() => setPending(null)}
        title={copy?.title ?? ''}
        confirmLabel={copy?.confirm ?? 'Confirm'}
        {...(copy?.color !== undefined ? { confirmColor: copy.color } : {})}
        confirmDisabled={copy?.input !== undefined && text.trim().length < 3}
        loading={act.isPending}
        error={act.error instanceof Error ? act.error.message : null}
        onConfirm={() => {
          if (pending !== null) act.mutate({ action: pending.action, payoutId: pending.payoutId, input: text.trim() });
        }}
      >
        <Text size="sm">{pending?.label}</Text>
        {copy?.input !== undefined && (
          <Textarea
            label={copy.input === 'reason' ? 'Reason (kept in the audit log)' : 'What was done (kept in the audit log)'}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            autosize
            minRows={2}
          />
        )}
      </AdminActionModal>
    </AdminShell>
  );
}
